/**
 * Zod Validation Schemas for Shopify API Responses
 * 
 * Runtime validation to ensure external API data matches expected types
 */

import { z } from 'zod';

/**
 * Price schema
 */
const PriceSchema = z.object({
  amount: z.string(),
  currencyCode: z.string(),
});

/**
 * Image node schema
 */
const ImageNodeSchema = z.object({
  url: z.string().url(),
  altText: z.string().nullable(),
});

/**
 * Product option schema
 */
const SelectedOptionSchema = z.object({
  name: z.string(),
  value: z.string(),
});

/**
 * Product variant node schema
 */
const VariantNodeSchema = z.object({
  id: z.string(),
  title: z.string(),
  price: PriceSchema,
  availableForSale: z.boolean(),
  selectedOptions: z.array(SelectedOptionSchema),
});

/**
 * Product option schema
 */
const ProductOptionSchema = z.object({
  name: z.string(),
  values: z.array(z.string()),
});

/**
 * Product node schema
 */
const ProductNodeSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  handle: z.string(),
  priceRange: z.object({
    minVariantPrice: PriceSchema,
  }),
  images: z.object({
    edges: z.array(z.object({
      node: ImageNodeSchema,
    })),
  }),
  variants: z.object({
    edges: z.array(z.object({
      node: VariantNodeSchema,
    })),
  }),
  options: z.array(ProductOptionSchema),
});

/**
 * Single product edge schema
 */
export const ProductEdgeSchema = z.object({
  node: ProductNodeSchema,
});

/**
 * Products query response schema
 */
export const ProductsResponseSchema = z.object({
  data: z.object({
    products: z.object({
      edges: z.array(ProductEdgeSchema),
    }),
  }),
});

/**
 * Single product by handle response schema
 */
export const ProductByHandleResponseSchema = z.object({
  data: z.object({
    productByHandle: ProductNodeSchema.nullable(),
  }),
});

/**
 * Cart creation response schema
 */
export const CartCreateResponseSchema = z.object({
  data: z.object({
    cartCreate: z.object({
      cart: z.object({
        id: z.string(),
        checkoutUrl: z.string().url(),
        totalQuantity: z.number(),
        cost: z.object({
          totalAmount: PriceSchema,
        }),
      }),
      userErrors: z.array(z.object({
        field: z.array(z.string()),
        message: z.string(),
      })),
    }),
  }),
});

/**
 * Generic GraphQL error schema
 */
export const GraphQLErrorSchema = z.object({
  message: z.string(),
  locations: z.array(z.object({
    line: z.number(),
    column: z.number(),
  })).optional(),
  path: z.array(z.string()).optional(),
});

/**
 * Type exports for validated data
 */
export type ValidatedProductsResponse = z.infer<typeof ProductsResponseSchema>;
export type ValidatedProductByHandleResponse = z.infer<typeof ProductByHandleResponseSchema>;
export type ValidatedCartCreateResponse = z.infer<typeof CartCreateResponseSchema>;
